import { create } from 'zustand';
import textbookService from '../services/textbookService';

// 创建手动选择页面的状态管理
const useManualSelectionStore = create((set, get) => ({
  // 年级、学期和单元选择
  grade: '三年级',
  term: '上册',
  multiSelectMode: true,
  
  // 已选单元
  selectedUnits: [],
  
  // 所有可选单元 - 初始为空，将根据选择的年级和学期动态加载
  availableUnits: [],
  
  // 单词列表 - 初始为空，将根据选择的年级和学期动态加载
  words: [],
  
  // 听写设置 - 注意：这里只保留页面特定的设置，全局设置通过useSettings获取
  dictationSettings: {
    randomOrder: false, // 随机顺序设置，默认为按选择顺序
  },
    // 操作方法
    setGrade: (grade) => set((state) => {
      // 获取新选择的年级下可用的学期
      const availableTerms = textbookService.getTerms(grade);
      // 如果当前选择的学期在新年级中不可用，则选择第一个可用学期
      const newTerm = availableTerms.includes(state.term) ? state.term : availableTerms[0] || '上册';
      
      // 加载新年级和学期的课本数据
      const textbook = textbookService.getTextbook(grade, newTerm);
      const newUnits = textbook ? textbook.units.map(unit => unit.name) : [];
      
      // 重置单元选择和单词列表，使用统一的 loadWordsFromTextbook 函数
      return { 
        grade, 
        term: newTerm, 
        selectedUnits: [],
        availableUnits: newUnits,
        words: loadWordsFromTextbook(textbook)
      };
    }),
  
  setTerm: (term) => set((state) => {
    // 加载当前年级和新学期的课本数据
    const textbook = textbookService.getTextbook(state.grade, term);
    const newUnits = textbook ? textbook.units.map(unit => unit.name) : [];
    
    // 重置单元选择和单词列表
    return { 
      term, 
      selectedUnits: [],
      availableUnits: newUnits,
      words: loadWordsFromTextbook(textbook)
    };
  }),
  
  toggleMultiSelectMode: () => set((state) => ({ multiSelectMode: !state.multiSelectMode })),
  
  // 单元选择相关方法
  toggleUnit: (unit) => set((state) => {
    const isSelected = state.selectedUnits.includes(unit);
    const newSelectedUnits = isSelected 
      ? state.selectedUnits.filter(u => u !== unit)
      : [...state.selectedUnits, unit];
    return { selectedUnits: newSelectedUnits };
  }),
  
  clearSelectedUnits: () => set({ selectedUnits: [] }),
  
  selectAllUnits: () => set((state) => ({ 
    selectedUnits: [...state.availableUnits] 
  })),
  
  // 单词选择相关方法
  toggleWord: (wordId) => set((state) => ({
    words: state.words.map(word => 
      word.id === wordId ? { ...word, selected: !word.selected } : word
    )
  })),
  
  // 修改全选单词方法，接受一个参数来更新单词列表
  selectAllWords: (updatedWords) => set((state) => ({
    words: updatedWords || state.words.map(word => ({ ...word, selected: true }))
  })),
  
  // 添加切换随机顺序的方法
  toggleRandomOrder: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      randomOrder: !state.dictationSettings.randomOrder
    }
  })),
  
  // 模态框状态
  modal: {
    isOpen: false,
    type: null, // 'grade', 'term', 'mode'
  },
  
  // 模态框选项 - 从统一数据源获取
  modalOptions: {
    grade: textbookService.getGrades(),
    term: ['上册', '下册'],
    mode: ['单选模式', '多选模式'],
  },
  
  // 打开模态框
  openModal: (type) => set((state) => { 
    // 如果是打开年级选择，更新可用年级列表
    if (type === 'grade') {
      return {
        modal: { isOpen: true, type },
        modalOptions: {
          ...state.modalOptions,
          grade: textbookService.getGrades()
        }
      };
    }
    // 如果是打开学期选择，更新可用学期列表
    else if (type === 'term') {
      return {
        modal: { isOpen: true, type },
        modalOptions: {
          ...state.modalOptions,
          term: textbookService.getTerms(state.grade)
        }
      };
    }
    // 其他类型模态框
    return { 
      modal: { isOpen: true, type }
    };
  }),
  
  // 关闭模态框
  closeModal: () => set({ 
    modal: { 
      isOpen: false, 
      type: null 
    } 
  }),
  
  // 初始化数据
  initData: () => set((state) => {
    const textbook = textbookService.getTextbook(state.grade, state.term);
    const newUnits = textbook ? textbook.units.map(unit => unit.name) : [];
    
    return {
      availableUnits: newUnits,
      words: loadWordsFromTextbook(textbook)
    };
  })
}));

// 辅助函数：从课本数据加载单词列表
function loadWordsFromTextbook(textbook) {
  if (!textbook) return [];
  
  let wordsList = [];
  let colorMap = {
    'Unit 1': 'blue',
    'Unit 2': 'green',
    'Unit 3': 'yellow',
    'Unit 4': 'red',
    'Unit 5': 'purple',
    'Unit 6': 'orange',
    'Unit 7': 'pink',
    'Unit 8': 'teal'
  };
  
  textbook.units.forEach(unit => {
    const unitWords = unit.words
    .filter(word => !word.type || word.type === 'talk' || word.type === 'spell')
    .map(word => ({
      id: word.id,
      word: word.word,
      unit: unit.name,
      selected: false,
      color: colorMap[unit.name] || 'gray',
      // 添加额外信息
      phonetic: word.phonetic,
      translation: word.translation,
      partOfSpeech: word.partOfSpeech,
      difficulty: word.difficulty,
      type: word.type||'talk',
    }));
    
    wordsList = [...wordsList, ...unitWords];
  });
  
  return wordsList;
}

// 初始化数据
useManualSelectionStore.getState().initData();

export default useManualSelectionStore;